// Table
import Table from '@/components/ui/table/Table'

// Mantine
import { Anchor } from '@mantine/core'

// UI
import Badge from '@/components/ui/Badge'

// Cells
const IdCell = (item) => {
    return (
        <div className="table-cell p-5">
            <Badge color="gray">{item.id}</Badge>
        </div>
    )
}
const TitleCell = (item) => {
    const variant_name = item.shopify_product.title + ' - ' + item.title
    const url = `/products/shopify/${item.shopify_product_id}?variant_id=${item.id}`

    return (
        <div className="table-cell p-5">
            <Anchor href={url}>
                <span className="truncate">{variant_name}</span>
            </Anchor>
        </div>
    )
}

const SKUCell = (item) => {
    if (!item.sku) {
        return (
            <div className="table-cell p-5">
                <Badge color="red">SKU manquant</Badge>
            </div>
        )
    }

    return (
        <div className="table-cell p-5">
            <Badge color="gray">{item.sku}</Badge>
        </div>
    )
}

const StatusCell = (item) => {
    const status = item.shopify_product.status

    if (status === 'ACTIVE') {
        return (
            <div className="table-cell p-5">
                <Badge color="green">Actif</Badge>
            </div>
        )
    }

    if (status === 'DRAFT') {
        return (
            <div className="table-cell p-5">
                <Badge color="orange">Brouillon</Badge>
            </div>
        )
    }

    return (
        <div className="table-cell p-5">
            <Badge color="gray">Archivé</Badge>
        </div>
    )
}

const CustomCodesCell = (item) => {
    const is_valid = item.country_code_of_origin && item.harmonized_system_code

    if (!is_valid) {
        return (
            <div className="table-cell p-5">
                <Badge color="red">Non renseigné</Badge>
            </div>
        )
    }

    return (
        <div className="table-cell p-5">
            <Badge color="gray">
                {item.country_code_of_origin} - {item.harmonized_system_code}
            </Badge>
        </div>
    )
}

const WeightCell = (item) => {
    if (!item.weight) {
        return (
            <div className="table-cell p-5">
                <Badge color="red">Non renseigné</Badge>
            </div>
        )
    }

    return (
        <div className="table-cell p-5">
            <Badge color="gray">{item.weight} g</Badge>
        </div>
    )
}

const WatchClockDetailsCell = (item) => {
    const has_watch_clock_details =
        item.wcd_power && item.wcd_jewels && item.wcd_strap && item.wcd_case

    if (!has_watch_clock_details) {
        return (
            <div className="table-cell p-5">
                <Badge color="gray">Sans WCD</Badge>
            </div>
        )
    }

    return (
        <div className="table-cell p-5">
            <Badge color="green">WCD rempli</Badge>
        </div>
    )
}

export default function ShopifyVariantsTable() {
    // Set columns available for the table
    const availableColumns = [
        {
            name: 'ID',
            key: 'id',
            display: 'mandatory',
            sortable: true,
            component: IdCell,
        },
        {
            name: 'Nom',
            key: 'title',
            display: 'optional_default',
            sortable: true,
            component: TitleCell,
        },
        {
            name: 'SKU',
            key: 'sku',
            display: 'optional_default',
            sortable: true,
            component: SKUCell,
        },
        {
            name: 'Statut Shopify',
            key: 'status',
            display: 'optional_default',
            sortable: true,
            component: StatusCell,
        },
        {
            name: 'Codes douaniers',
            key: 'country_code_of_origin',
            display: 'optional_default',
            sortable: true,
            component: CustomCodesCell,
        },
        {
            name: 'Poids',
            key: 'weight',
            display: 'optional_default',
            sortable: true,
            component: WeightCell,
        },
        {
            name: 'Watch Clock Details',
            key: 'watch_clock_details',
            display: 'optional_default',
            sortable: true,
            component: WatchClockDetailsCell,
        },
    ]

    // Set available filters for the table
    const availableFilters = [
        {
            name: 'Statut Shopify',
            key: 'shopify_product_status',
            type: 'multiselect',
            placeholder: 'Actif, brouillon ou archivé',
            options: [
                { label: 'Actif', value: 'ACTIVE' },
                { label: 'Brouillon', value: 'DRAFT' },
                { label: 'Archivé', value: 'ARCHIVED' },
            ],
        },
        {
            name: 'SKU',
            key: 'sku_presence',
            type: 'select',
            placeholder: 'Rechercher un SKU',
            options: [
                { label: 'Avec SKU', value: 'with_sku' },
                { label: 'Sans SKU', value: 'without_sku' },
            ],
        },
        {
            name: 'Codes douaniers',
            key: 'custom_codes_presence',
            type: 'select',
            placeholder: 'Rechercher un code douanier',
            options: [
                {
                    label: 'Avec code douanier',
                    value: 'with_custom_codes',
                },
                {
                    label: 'Sans code douanier',
                    value: 'without_custom_codes',
                },
            ],
        },
        {
            name: 'Poids',
            key: 'weight_presence',
            type: 'select',
            placeholder: 'Avec ou sans poids',
            options: [
                { label: 'Renseigné', value: 'with_weight' },
                { label: 'Non renseigné', value: 'without_weight' },
            ],
        },
        {
            name: 'Watch Clock Details',
            key: 'watch_clock_details_presence',
            type: 'select',
            placeholder: 'Rechercher un WCD',
            options: [
                { label: 'Avec WCD', value: 'with_wcd' },
                { label: 'Sans WCD', value: 'without_wcd' },
            ],
        },
        {
            name: 'Associé à un produit du catalogue',
            key: 'associated_to_catalog_product',
            type: 'select',
            placeholder: 'Rechercher un produit du catalogue',
            options: [
                { label: 'Oui', value: 'yes' },
                { label: 'Non', value: 'no' },
            ],
        },
    ]

    return (
        <Table
            searchable={false}
            availableColumns={availableColumns}
            availableFilters={availableFilters}
            engineApiEndpoint="/shopify/product-variants"
            defaultSortColumn="id"
            defaultSortDirection="desc"
        />
    )
}
